import { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import type { Alert, AlertAudience, AlertCategory } from "@/services/api/guardiansv2/types";
import { useMissionControlSubscription } from "@/context/MissionControlWebSocketContext";

const PAGE_SIZE = 50;

export interface UseAlertsOptions {
  enabled?: boolean;
  missionId?: string | null;
  templateId?: string | null;
  taskId?: string | null;
  agentId?: string | null;
  catalogueId?: string | null;
  category?: AlertCategory | null;
  audience?: AlertAudience | null;
}

export interface UseAlertsResult {
  alerts: Alert[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  refresh: () => void;
  loadMore: () => void;
  archive: (alertId: string) => Promise<void>;
}

/**
 * Open alerts list with WS refresh on agentic failures.
 */
export function useAlerts({
  enabled = true,
  missionId = null,
  templateId = null,
  taskId = null,
  agentId = null,
  catalogueId = null,
  category = null,
  audience = null,
}: UseAlertsOptions = {}): UseAlertsResult {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listParams = useMemo(
    () => ({
      mission_id: missionId || undefined,
      template_id: templateId || undefined,
      task_id: taskId || undefined,
      agent_id: agentId || undefined,
      catalogue_id: catalogueId || undefined,
      category: category || undefined,
      audience: audience || undefined,
    }),
    [agentId, audience, catalogueId, category, missionId, templateId, taskId]
  );

  const fetchList = useCallback(async (append: boolean, currentSkip: number) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await GuardiansV2API.listAlerts({
        status: "open",
        time_range: "7d",
        skip: currentSkip,
        limit: PAGE_SIZE,
        ...listParams,
      });
      setAlerts((prev) => (append ? [...prev, ...res.alerts] : res.alerts));
      setTotal(res.total);
      setSkip(currentSkip + res.alerts.length);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load alerts");
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [listParams]);

  const refresh = useCallback(() => {
    setSkip(0);
    void fetchList(false, 0);
  }, [fetchList]);

  const debouncedRefresh = useMemo(
    () => debounce(() => refresh(), 500),
    [refresh]
  );

  useEffect(() => {
    if (!enabled) {
      setAlerts([]);
      setTotal(0);
      setSkip(0);
      setLoading(false);
      setLoadingMore(false);
      setError(null);
      return;
    }
    setSkip(0);
    void fetchList(false, 0);
    return () => debouncedRefresh.cancel();
  }, [enabled, fetchList, debouncedRefresh]);

  useMissionControlSubscription({
    onTaskStatusChanged: (payload) => {
      if (payload.new_status === "blocked" || payload.new_status === "failed") {
        debouncedRefresh();
      }
    },
    onInteractionCompleted: (payload) => {
      if (payload.status === "failed") {
        debouncedRefresh();
      }
    },
    onAlertRaised: () => {
      debouncedRefresh();
    },
  });

  const hasMore = skip < total;

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return;
    void fetchList(true, skip);
  }, [hasMore, loading, loadingMore, fetchList, skip]);

  const archive = useCallback(async (alertId: string) => {
    setAlerts((prev) => prev.filter((a) => a._id !== alertId));
    setTotal((t) => Math.max(0, t - 1));
    try {
      await GuardiansV2API.archiveAlert(alertId);
    } catch {
      refresh();
    }
  }, [refresh]);

  return {
    alerts,
    loading,
    loadingMore,
    error,
    hasMore,
    refresh,
    loadMore,
    archive,
  };
}
